import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SocialLogin from "../SocialLogin/SocialLogin";
import { useTranslation } from "react-i18next";
import { signUpApi, sendotp } from "../../../api/user";
import { toast } from "react-toastify";
import PageContainer from "../../../components/pages/PageContainer";
import { event } from "../../../utils/event";
import { setSidebarCollapseOverride } from "../../../utils/sidebarCollapse";
import {
  hasLegalAccepted,
  legalAcceptedFlag,
} from "../../../utils/legalAcceptance";
import {
  setPendingSignupEmail,
  setVerificationToken,
} from "../../../utils/pendingSignup";
// Shared with the reset-password screen so the rule and the line describing it
// cannot drift apart between the two forms that enforce it.
import {
  PASSWORD_HINT,
  PASSWORD_ERROR,
  isStrongPassword,
} from "../../../utils/passwordRules";
import qushiLogo from "../../../images/logo-green.png";
import useAuthScreenBackground from "../../../hooks/useAuthScreenBackground";
import ProcessingButton from "../../../components/Skeleton/ProcessingButton";

// Where a referral code waits while the user is sent back to tick the legal box.
const PENDING_REFERRAL_KEY = "pending_referral_code";

const QushiLogo = () => (
  <img src={qushiLogo} alt="QUSHi" style={{ width: "120px" }} />
);

function RegisterIndex() {
  // Cream all the way to the screen edges - see the hook for why the wrapper
  // div alone could not reach behind the status bar and home indicator.
  useAuthScreenBackground();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobileView] = useState(window.innerWidth < 768);

  const { referralCode: referralCodeFromParams } = useParams();
  const [referralCode, setReferralCode] = useState("");

  useEffect(() => {
    if (referralCodeFromParams) {
      setReferralCode(referralCodeFromParams);
      // Survives the redirect below - a referral link lands here directly, and
      // the code must still be attached to the signup after the detour through
      // the account creation screen.
      try {
        localStorage.setItem(PENDING_REFERRAL_KEY, referralCodeFromParams);
      } catch (e) {}
    } else {
      try {
        const saved = localStorage.getItem(PENDING_REFERRAL_KEY);
        if (saved) setReferralCode(saved);
      } catch (e) {}
    }
  }, [referralCodeFromParams]);

  // The email form is a continuation of the account creation screen, never an
  // entry point of its own. Reaching it without the legal + age acceptance -
  // a referral link, a bookmark, a shared URL - sends the user back to that
  // screen rather than into a signup the backend would refuse anyway.
  useEffect(() => {
    if (!hasLegalAccepted()) {
      navigate("/auth/create-account", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    event("sendDataToReactApp");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email || email === "") {
      setError({ email: ["Please enter your email"] });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError({ email: ["Invalid email address"] });
      return;
    }
    if (!password || password === "") {
      setError({ password: ["Please enter your password"] });
      return;
    }

    if (!isStrongPassword(password)) {
      // The rules are already on screen under the field. Repeating them here -
      // and listing every accepted symbol - only told the user what they could
      // already see, so the failure message just points back at them.
      setError({ password: [PASSWORD_ERROR] });
      return;
    }

    setError(null);
    setLoading(true);

    const body = {
      email: email,
      password: password,
      referral_code: referralCodeFromParams || referralCode || null,
      // The checkbox from the account creation screen. The backend refuses the
      // signup without it, so this is not decoration.
      legal_accepted: legalAcceptedFlag(),
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "sign_up_start", method: "email" });

    try {
      const response = await signUpApi(body);
      if (response.data.status === 200) {
        const accessToken = response.data.data.access_token;
        localStorage.setItem("user_id", response.data.data.id);
        localStorage.setItem("accessToken", JSON.stringify(accessToken));
        setSidebarCollapseOverride(null);

        // Remember the address BEFORE requesting the code. If send-otp fails or
        // is rate-limited, the verification screen still knows who it is
        // verifying and Resend Code still works - which is exactly what used to
        // break.
        setPendingSignupEmail(response.data.data.email);
        try {
          localStorage.removeItem(PENDING_REFERRAL_KEY);
        } catch (e) {}

        const ema = { email: response.data.data.email, otp_purpose: 2 };
        const respo = await sendotp(ema);
        setVerificationToken(respo?.data?.data?.email_token);
        if (respo?.data?.status === 200) {
          window.dataLayer.push({ event: "email_verification_sent", method: "email" });
        }

        setLoading(false);
        // Straight to verification. The Legal Disclaimer popup that used to sit
        // in between is gone - its consent is now the checkbox on the account
        // creation screen, recorded at signup.
        navigate("/auth/verification");
      } else if (response.data.status === 400) {
        setLoading(false);
        toast.error(
          response.data.message ===
            "Please confirm your agreement and age eligibility to continue."
            ? "Confirm the Terms of Service, Privacy Policy, and age requirement."
            : "Could not create your account. Try again.",
        );
      }
    } catch (error) {
      setLoading(false);
      const apiError = error?.response?.data?.error || "";
      toast.error(
        apiError.toLowerCase().includes("already been taken")
          ? "An account with this email already exists. Log in the way you signed up, or contact support."
          : "Could not create your account. Check your information and try again.",
      );
    }
  };

  if (isMobileView) {
    return (
      <>
        <PageContainer>
          <div
            className="min-h-screen w-full flex flex-col px-8 pt-12 pb-8"
            style={{ backgroundColor: "#F2F0EC" }}
          >
            <div className="flex-1 flex flex-col justify-center items-center">
              <h1 className="text-primaryColor font-bold text-4xl mb-4 font-montserrat">
                Almost there
              </h1>
              <p className="text-greenXD font-semibold mb-8 font-montserrat">
                No brokerage account or credit card required.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <input
                    id="email"
                    type="text"
                    name="email"
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("Email")}
                    className="w-full rounded-full bg-gray-200 placeholder-gray-500 text-gray-700 px-5 py-4 focus:outline-none"
                  />
                  {error?.email && (
                    <span className="block text-red-500 text-xs mt-1 ml-2">
                      {error.email[0]}
                    </span>
                  )}
                </div>
                <div className="mb-6">
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("Password")}
                      className="w-full rounded-full bg-gray-200 placeholder-gray-500 text-gray-700 px-5 py-4 pr-12 focus:outline-none"
                    />
                    <div
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                      />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2 ml-2 font-montserrat">
                    {PASSWORD_HINT}
                  </p>
                  {error?.password && (
                    <span className="block text-red-500 text-xs mt-1 ml-2">
                      {error.password[0]}
                    </span>
                  )}
                </div>
                <ProcessingButton
                  processing={loading}
                  type="submit"
                  label="Create account"
                  processingLabel="Creating"
                  className="w-full bg-primaryColor text-white rounded-2xl py-4 font-bold text-lg focus:outline-none active:opacity-80 transition"
                />
              </form>
              <div className="onboarding-social mt-6">
                <SocialLogin />
              </div>
              <div className="text-center mt-6">
                <span className="text-gray-600 font-montserrat">
                  {t("already_have_account")}?{" "}
                </span>
                <Link
                  to="/auth/login"
                  className="text-primaryColor font-bold underline font-montserrat"
                >
                  {t("Log in")}
                </Link>
              </div>
            </div>
            <div className="flex justify-center py-6">
              <QushiLogo />
            </div>
          </div>
        </PageContainer>
      </>
    );
  }

  return (
    <>
      <PageContainer>
        <div className="flex min-h-screen">
          <div className="w-1/2 bg-primaryColor flex flex-col items-center justify-center">
            <img src="/Logo.png" alt="QUSHi" className="w-64" />
            <p
              className="text-white text-sm mt-4 uppercase whitespace-nowrap"
              style={{ letterSpacing: "0.3em", marginRight: "-0.3em" }}
            >
              TRADE TRACK PROVE IT
            </p>
          </div>

          <div
            className="w-1/2 flex items-center justify-center px-12 py-12 min-h-screen"
            style={{ backgroundColor: "#F2F0EC" }}
          >
            <div className="w-full max-w-sm">
              <h1 className="text-primaryColor font-bold text-4xl mb-4 font-montserrat">
                Almost there
              </h1>
              <p className="text-greenXD font-semibold mb-8 font-montserrat">
                No brokerage account or credit card required.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <input
                    id="email"
                    type="text"
                    name="email"
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("Email")}
                    className="w-full rounded-full bg-gray-200 placeholder-gray-500 text-gray-700 px-5 py-4 focus:outline-none"
                  />
                  {error?.email && (
                    <span className="block text-red-500 text-xs mt-1 ml-2">
                      {error.email[0]}
                    </span>
                  )}
                </div>
                <div className="mb-6">
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("Password")}
                      className="w-full rounded-full bg-gray-200 placeholder-gray-500 text-gray-700 px-5 py-4 pr-12 focus:outline-none"
                    />
                    <div
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                      />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2 ml-2 font-montserrat">
                    {PASSWORD_HINT}
                  </p>
                  {error?.password && (
                    <span className="block text-red-500 text-xs mt-1 ml-2">
                      {error.password[0]}
                    </span>
                  )}
                </div>
                <ProcessingButton
                  processing={loading}
                  type="submit"
                  label="Create account"
                  processingLabel="Creating"
                  className="w-full bg-primaryColor text-white rounded-2xl py-4 font-bold text-lg focus:outline-none active:opacity-80 transition"
                />
              </form>
              <SocialLogin />
              <div className="text-center mt-6">
                <span className="text-gray-600 font-montserrat">
                  {t("already_have_account")}?{" "}
                </span>
                <Link
                  to="/auth/login"
                  className="text-primaryColor font-bold underline font-montserrat"
                >
                  {t("Log in")}
                </Link>
              </div>
              <div className="flex justify-center mt-10">
                <QushiLogo />
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    </>
  );
}

export default RegisterIndex;
